<template lang="pug">
.FStepCards
  .FStepCards__desktopNavigation(
    v-if="navigationType !== 'slider'"
    :style="desktopNavigationStyle"
  )
    FStepCard.FStepCards__desktopCard(
      v-for="(card, index) in cards"
      ref="cardsRef"
      :key="card.title"
      v-bind="card"
      :step-number="index + 1"
      :highlighted="isMounted && navigationType === 'desktop-small-height' && index === currentStep"
      :hidden="isCardHidden(index)"
    )
  FCardsSlider(
    v-else
    ref="sliderRef"
    v-model="currentStep"
    :card-mobile-width="304"
    slide-to-end
  )
    template(#cards)
      FStepCard(
        v-for="(card, index) in cards"
        :key="card.title"
        v-bind="card"
        :step-number="index + 1"
        :highlighted="index === currentStep"
        @click="currentStep = index"
      )
</template>

<style lang="stylus">
.FStepCards__desktopNavigation
  display flex
  flex-direction column
  row-gap rem(32)
  transform var(--FStepCards--desktopNavigation--transform)
  transition transform 0.3s var(--transition--ease-out)
  position relative

  &::before
    content ''
    position absolute
    width var(--FStepCards--desktopNavigation--highlighterWidth)
    display var(--FStepCards--desktopNavigation--highlighterDisplay)
    height var(--FStepCards--desktopNavigation--highlighterHeight)
    top var(--FStepCards--desktopNavigation--highlighterTop)
    background 'rgba(%s, 0.6)' % var(--color--secondary--light-2--rgb)
    border-radius rem(16)
    transition top 0.5s var(--transition--ease-out), height 0.5s var(--transition--ease-out)

.FStepCards__desktopCard
  cursor unset
</style>

<script setup lang="ts">
import FCardsSlider from '@/components/organisms/FCardsSlider.vue';
import FStepCard from '@/components/molecules/FStepCard.vue';

import type { FStepCardProps } from '@/components/molecules/FStepCard.vue';

export interface FStepCardsProps {
  /**
   * Handles the index
   */
  modelValue: number;
  /**
   * Array of step cards
   **/
  cards?: Omit<FStepCardProps, 'stepNumber'>[];
}

const props = withDefaults(defineProps<FStepCardsProps>(), {
  cards: () => [],
});

const currentStep = useVModelProxy({ props, defaultValue: 0 });

const isMounted = useMounted();
const { height } = useWindowSize();
const { isSmAndDown } = useFBreakpoints();

const cardsTranslateY = computed(() => {
  return cardsBounding.value && navigationType.value === 'desktop-small-height'
    ? cardsBounding.value
        .slice(
          0,
          // Do not translate anymore once the before last card is reached
          currentStep.value === props.cards.length - 1
            ? currentStep.value - 1
            : currentStep.value
        )
        .reduce((total, current) => total - current.height - 32, 0)
    : 0;
});

const sliderRef = ref<InstanceType<typeof FCardsSlider>>();

const navigationType = computed(() => {
  if (isSmAndDown.value) {
    return 'slider';
  } else {
    if (height.value < 1100) {
      return 'desktop-small-height';
    }
  }
  return 'desktop';
});

function isCardHidden(index: number): boolean {
  // Always have the last 2 cards visible
  if (index >= props.cards.length - 2) return false;

  return (
    navigationType.value === 'desktop-small-height' && currentStep.value > index
  );
}
const cardsRef = ref<InstanceType<typeof FStepCard>[]>();

const cardsBounding = computed(() => {
  return cardsRef.value?.map(card => {
    const element = card.$el as HTMLElement;
    const rectangle = element.getBoundingClientRect();
    return {
      width: rectangle.width,
      height: rectangle.height,
    };
  });
});

const highlighterTop = computed(() =>
  cardsBounding.value
    ?.slice(0, currentStep.value)
    .reduce((total, current) => total + current.height + 32, 0)
);

const desktopNavigationStyle = computed(() => ({
  '--FStepCards--desktopNavigation--transform': `translateY(${genSize(
    cardsTranslateY.value
  )})`,
  '--FStepCards--desktopNavigation--highlighterDisplay':
    navigationType.value === 'desktop' ? 'block' : 'none',
  '--FStepCards--desktopNavigation--highlighterWidth': genSize(
    cardsBounding.value?.[currentStep.value].width
  ),
  '--FStepCards--desktopNavigation--highlighterHeight': genSize(
    cardsBounding.value?.[currentStep.value].height
  ),
  '--FStepCards--desktopNavigation--highlighterTop': genSize(
    highlighterTop.value
  ),
}));
</script>
